/**
 * Versioning preventivi — gestione catena di revisioni.
 *
 * useEstimateRevisions(estimateId): ritorna tutte le versioni della catena
 *   (preventivo originale + tutti i figli/nipoti, ordinati per version).
 *
 * useCreateRevision(): mutation che duplica un preventivo come nuova revisione
 *   con parent_estimate_id puntato all'originale + version incrementato.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export interface RevisionSummary {
  id: string;
  name: string;
  status: string;
  version: number;
  parent_estimate_id: string | null;
  created_at: string;
  total_amount: number | null;
}

/**
 * Risale alla "radice" della catena (il preventivo originale senza parent)
 * e poi raccoglie tutti i discendenti. Funziona in modo iterativo (evita CTE
 * ricorsive: il caso d'uso è < 20 revisioni, brute force è ok).
 */
export const useEstimateRevisions = (estimateId: string | null | undefined) => {
  return useQuery({
    queryKey: ['estimate-revisions', estimateId],
    queryFn: async (): Promise<RevisionSummary[]> => {
      if (!estimateId) return [];

      // 1. Trova la radice risalendo i parent
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let currentId: string | null = estimateId;
      let rootId: string = estimateId;
      const visited = new Set<string>();
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any)
          .from('estimates')
          .select('id, parent_estimate_id')
          .eq('id', currentId)
          .maybeSingle();
        if (error || !data) break;
        if (!data.parent_estimate_id) {
          rootId = data.id;
          break;
        }
        currentId = data.parent_estimate_id;
        rootId = data.parent_estimate_id;
      }

      // 2. Raccoglie tutti i preventivi nella catena (BFS dai discendenti partendo dalla radice)
      const chain: RevisionSummary[] = [];
      const queue: string[] = [rootId];
      const seenIds = new Set<string>();
      while (queue.length > 0) {
        const id = queue.shift()!;
        if (seenIds.has(id)) continue;
        seenIds.add(id);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: node, error: nodeError } = await (supabase as any)
          .from('estimates')
          .select('id, name, status, version, parent_estimate_id, created_at, total_amount')
          .eq('id', id)
          .maybeSingle();
        if (nodeError || !node) continue;
        chain.push(node as RevisionSummary);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: children } = await (supabase as any)
          .from('estimates')
          .select('id')
          .eq('parent_estimate_id', id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (children ?? []).forEach((c: any) => {
          if (c?.id) queue.push(c.id);
        });
      }

      return chain.sort((a, b) => (a.version ?? 0) - (b.version ?? 0));
    },
    enabled: !!estimateId,
    staleTime: 30_000,
  });
};

interface CreateRevisionInput {
  /** Preventivo sorgente (di cui si crea una revisione). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sourceEstimate: any;
  /** Stratigrafie del sorgente, da copiare. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sourceStratigraphies: any[];
}

export const useCreateRevision = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async ({ sourceEstimate, sourceStratigraphies }: CreateRevisionInput) => {
      const baseName = (sourceEstimate.name ?? 'Preventivo').replace(/\s*\(rev\s*\d+\)\s*$/, '').trim();
      const newVersion = (Number(sourceEstimate.version) || 1) + 1;
      const newName = `${baseName} (rev ${newVersion})`;

      // 1. Crea nuovo estimate draft con parent
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: created, error: estErr } = await (supabase as any)
        .from('estimates')
        .insert({
          name: newName,
          description: sourceEstimate.description,
          project_id: sourceEstimate.project_id ?? sourceEstimate.projectId,
          status: 'draft',
          version: newVersion,
          total_amount: sourceEstimate.total_amount ?? 0,
          parent_estimate_id: sourceEstimate.id,
          notes: sourceEstimate.notes,
          internal_notes: sourceEstimate.internal_notes,
        })
        .select()
        .single();
      if (estErr || !created) throw estErr || new Error('Errore creazione revisione');

      // 2. Duplica stratigrafie
      for (const s of sourceStratigraphies) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: stratErr } = await (supabase as any)
          .from('estimate_stratigraphies')
          .insert({
            estimate_id: created.id,
            stratigraphy_id: s.stratigraphy_id ?? null,
            name: s.name,
            description: s.description,
            area: s.area,
            quantity: s.quantity,
            unit_cost: s.unit_cost,
            total_cost: s.total_cost,
            stratigraphy_data: s.stratigraphy_data ?? null,
            layers_data: s.layers_data ?? null,
            prices_updated_at: s.prices_updated_at ?? new Date().toISOString(),
            is_snapshot: true,
            original_stratigraphy_id: s.original_stratigraphy_id ?? s.stratigraphy_id ?? null,
            finish_level: s.finish_level ?? undefined,
            finish_cost_per_sqm: s.finish_cost_per_sqm ?? undefined,
            finish_components_data: s.finish_components_data ?? undefined,
            wall_height: s.wall_height ?? undefined,
            calculated_materials: s.calculated_materials ?? undefined,
            stud_spacing_mm: s.stud_spacing_mm ?? undefined,
            frame_coupling: s.frame_coupling ?? undefined,
          });
        if (stratErr) throw stratErr;
      }

      return created;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (created: any) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-revisions'] });
      toast.success(`Revisione creata: ${created.name}`);
      // Naviga al nuovo preventivo
      if (created?.id) navigate(`/estimates/${created.id}/manage`);
    },
    onError: (e: Error) => {
      console.error('[useCreateRevision] error:', e);
      toast.error(e.message || 'Errore durante la creazione della revisione');
    },
  });

  return {
    createRevision: mutation.mutate,
    createRevisionAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
  };
};
